/**
 * PreloadScene.js - Asset Loading Scene for Memex Racing
 * 
 * Handles loading all game assets and programmatically creates pixel art sprites
 * for the retro racing game aesthetic.
 */

import Phaser from 'phaser';
import { MusicManager } from '../systems/MusicManager';

// REMOVED: Webpack imports causing Phaser URL string errors
// import mainMenuBackground from '../../../public/assets/backgrounds/main-menu-background.png';
// import lobbyBackground from '../../../public/assets/backgrounds/race-lobby.jpeg';
// DISABLED: UIIntegration was causing DOM conflicts and loading loop
// import { UIIntegration } from './UIIntegration';

export class PreloadScene extends Phaser.Scene {
    constructor() {
        super({ key: 'PreloadScene' });
    }

    preload() {
        console.log('[PreloadScene] Starting asset preload...');
        
        // Initialize music manager
        this.musicManager = new MusicManager(this);
        
        // Extend timeout for large sprite files (60 seconds)
        this.load.timeout = 60000;
        console.log('[PreloadScene] Extended load timeout to 60 seconds for large sprites');
        
        // Create loading text
        const loadingText = this.add.text(960, 540, 'Loading...', {
            fontSize: '32px',
            fontFamily: 'Courier New',
            color: '#00ff00',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        
        // Add debug text to show what's loading
        const debugText = this.add.text(960, 640, '', {
            fontSize: '16px',
            fontFamily: 'Courier New',
            color: '#ffff00'
        }).setOrigin(0.5);

        const progressBar = this.add.graphics();
        const progressBox = this.add.graphics();
        
        // Progress bar background
        progressBox.fillStyle(0x222222);
        progressBox.fillRect(760, 580, 400, 50);
        
        // Progress bar fill
        progressBar.fillStyle(0x0088ff); // Changed from green to blue

        // Update progress bar during loading
        this.load.on('progress', (value) => {
            progressBar.clear();
            progressBar.fillStyle(0x0088ff); // Changed from green to blue
            progressBar.fillRect(760, 580, 400 * value, 50);
            
            // Update loading text with percentage
            const percentage = Math.round(value * 100);
            loadingText.setText(`Loading... ${percentage}%`);
        });
        
        // Track file loading
        this.load.on('fileprogress', (file) => {
            if (debugText) {
                debugText.setText(`Loading: ${file.key}`);
            }
        });

        this.load.on('complete', () => {
            console.log('[PreloadScene] Asset loading complete');
            loadingText.destroy();
            if (debugText) debugText.destroy();
            progressBar.destroy();
            progressBox.destroy();
        });

        // Load any external assets (sounds, etc.) FIRST
        this.loadExternalAssets();
        
        // Load music tracks
        this.musicManager.preloadMusic(this);
        
        // Create minimal required textures for UI elements and fallback sprites
        this.createMinimalUITextures();
        this.createEssentialSprites();  // Create M token and other essential sprites
        // REMOVED: Don't create fallback sprites prematurely - only create when images fail to load
        // this.createFallbackPlayerSprites();
    }

    create() {
        console.log('[PreloadScene] Create phase - ensuring button textures exist');
        
        // Store music manager in game registry for access by other scenes
        this.game.registry.set('musicManager', this.musicManager);
        
        // Ensure button textures exist before transitioning
        this.ensureButtonTextures();
        
        console.log('[PreloadScene] Environment:', (typeof process !== 'undefined' ? process.env.NODE_ENV : 'production') || 'production');
        console.log('[PreloadScene] Available textures:', Object.keys(this.textures.list));
        
        // CRITICAL: Validate background image loading
        this.validateBackgroundTextures();
        
        // If main background failed, try emergency reload
        this.attemptEmergencyBackgroundLoad();
        console.log('[PreloadScene] Available scenes:', this.scene.manager.keys);
        
        // Validate all required sprites BEFORE any transitions
        console.log('[PreloadScene] Validating required assets before transition...');
        this.validateRequiredAssets();
        
        try {
            // DISABLED UIIntegration - was causing DOM conflicts and loading loop
            // The UIIntegration was trying to manipulate DOM during Phaser initialization
            // which caused conflicts and prevented scene transitions
            /*
            console.log('[PreloadScene] Initializing new UI system...');
            const uiIntegration = new UIIntegration(this);
            this.game.uiIntegration = uiIntegration;
            uiIntegration.initializeUI().then(() => {
                console.log('[PreloadScene] ✅ New UI system initialized successfully');
            }).catch(error => {
                console.error('[PreloadScene] Failed to initialize UI:', error);
            });
            */
            
            // Validate that MainMenuScene is available
            if (!this.scene.manager.getScene('MainMenuScene')) {
                console.error('[PreloadScene] MainMenuScene not found in scene manager!');
                console.log('[PreloadScene] Available scenes:', Object.keys(this.scene.manager.keys));
                throw new Error('MainMenuScene not found');
            }
            
            console.log('[PreloadScene] ✅ MainMenuScene found, starting transition...');
            
            // Simple direct transition without complex async handling
            // Add flag to prevent duplicate transitions
            if (this.transitionStarted) {
                console.log('[PreloadScene] Transition already in progress, skipping...');
                return;
            }
            this.transitionStarted = true;
            
            // Add a small delay to ensure assets are settled
            this.time.delayedCall(100, () => {
                console.log('[PreloadScene] 🎬 Starting scene transition to LoginScene...');
                
                // Fade out loading screen and transition to login
                this.cameras.main.fadeOut(500, 0, 0, 0);
                
                this.cameras.main.once('camerafadeoutcomplete', () => {
                    console.log('[PreloadScene] 🎭 Camera fade complete, starting LoginScene...');
                    this.scene.start('LoginScene');
                });
            });
            
        } catch (error) {
            console.error('[PreloadScene] ❌ Error in create phase:', error);
            console.error('[PreloadScene] Error stack:', error.stack);
            
            // Immediate fallback transition without delay
            console.log('[PreloadScene] 🔄 Attempting immediate transition to LoginScene...');
            this.scene.start('LoginScene');
        }
    }

    /**
     * Create essential game sprites that must exist
     */
    createEssentialSprites() {
        const graphics = this.add.graphics();
        
        // Create M Token sprite (essential for game - golden M shape)
        if (!this.textures.exists('mtoken')) {
            graphics.clear();
            graphics.fillStyle(0xffd700);  // Gold color
            graphics.fillRect(32, 0, 64, 128);   // Vertical bar of M
            graphics.fillRect(0, 32, 128, 64);   // Horizontal bar of M
            graphics.fillRect(48, 16, 32, 32);   // Center highlight
            graphics.generateTexture('mtoken', 128, 128);
            console.log('[PreloadScene] Created M token texture');
        }
        
        // Create obstacle textures
        this.createObstacleTextures(graphics);
        
        // Create LARGE barrier textures
        this.createBarrierTextures(graphics);
        
        graphics.destroy();
    }
    
    /**
     * Create obstacle textures programmatically
     */
    createObstacleTextures(graphics) {
        // Oil slick - black circle with shine (larger size)
        if (!this.textures.exists('obstacle_oil')) {
            graphics.clear();
            graphics.fillStyle(0x222222);
            graphics.fillCircle(64, 64, 50);
            graphics.fillStyle(0x444444);
            graphics.fillCircle(50, 50, 20);
            graphics.generateTexture('obstacle_oil', 128, 128);
        }
        
        // Mud patch - brown irregular shape (larger size)
        if (!this.textures.exists('obstacle_mud')) {
            graphics.clear();
            graphics.fillStyle(0x654321);
            graphics.fillRect(20, 20, 88, 88);
            graphics.fillRect(10, 40, 108, 48);
            graphics.generateTexture('obstacle_mud', 128, 128);
        }
        
        // Rock barrier - gray rectangle
        if (!this.textures.exists('obstacle_rock')) {
            graphics.clear();
            graphics.fillStyle(0x808080);
            graphics.fillRect(8, 16, 48, 32);
            graphics.fillStyle(0x606060);
            graphics.fillRect(12, 20, 40, 24);
            graphics.generateTexture('obstacle_rock', 64, 64);
        }
        
        // Spike strip - silver with points
        if (!this.textures.exists('obstacle_spikes')) {
            graphics.clear();
            graphics.fillStyle(0xc0c0c0);
            graphics.fillRect(0, 24, 64, 16);
            for (let i = 0; i < 8; i++) {
                graphics.fillTriangle(i * 8 + 4, 24, i * 8, 32, i * 8 + 8, 32);
            }
            graphics.generateTexture('obstacle_spikes', 64, 64);
        }
        
        // Moving barrier - orange rectangle
        if (!this.textures.exists('obstacle_barrier')) {
            graphics.clear();
            graphics.fillStyle(0xff6600);
            graphics.fillRect(4, 20, 56, 24);
            graphics.fillStyle(0xffaa00);
            graphics.fillRect(8, 24, 48, 16);
            graphics.generateTexture('obstacle_barrier', 64, 64);
        }
        
        // Rotating blade - red star shape
        if (!this.textures.exists('obstacle_blade')) {
            graphics.clear();
            graphics.fillStyle(0xff0000);
            for (let i = 0; i < 4; i++) {
                const angle = (Math.PI * i) / 2;
                const x1 = 32 + Math.cos(angle) * 28;
                const y1 = 32 + Math.sin(angle) * 28;
                const x2 = 32 + Math.cos(angle + Math.PI/4) * 10;
                const y2 = 32 + Math.sin(angle + Math.PI/4) * 10;
                const x3 = 32 + Math.cos(angle - Math.PI/4) * 10;
                const y3 = 32 + Math.sin(angle - Math.PI/4) * 10;
                graphics.fillTriangle(x1, y1, x2, y2, x3, y3);
            }
            graphics.generateTexture('obstacle_blade', 64, 64);
        }
        
        // Laser beam - green glowing line
        if (!this.textures.exists('obstacle_laser')) {
            graphics.clear();
            graphics.fillStyle(0x00ff00);
            graphics.fillRect(0, 28, 64, 8);
            graphics.fillStyle(0x00ff00, 0.5);
            graphics.fillRect(0, 24, 64, 16);
            graphics.generateTexture('obstacle_laser', 64, 64);
        }
        
        // Trap door - brown square with handle
        if (!this.textures.exists('obstacle_trapdoor')) {
            graphics.clear();
            graphics.fillStyle(0x8b4513);
            graphics.fillRect(8, 8, 48, 48);
            graphics.fillStyle(0x654321);
            graphics.fillRect(12, 12, 40, 40);
            graphics.fillStyle(0x444444);
            graphics.fillCircle(32, 32, 6);
            graphics.generateTexture('obstacle_trapdoor', 64, 64);
        }
        
        // Ice patch - light blue with white highlights
        if (!this.textures.exists('obstacle_ice')) {
            graphics.clear();
            graphics.fillStyle(0x87ceeb);
            graphics.fillCircle(32, 32, 28);
            graphics.fillStyle(0xffffff, 0.6);
            graphics.fillCircle(24, 24, 12);
            graphics.fillCircle(40, 36, 8);
            graphics.generateTexture('obstacle_ice', 64, 64);
        }
        
        // Magnetic field - purple pulsing circle
        if (!this.textures.exists('obstacle_magnet')) {
            graphics.clear();
            graphics.lineStyle(4, 0xff00ff);
            graphics.strokeCircle(32, 32, 28);
            graphics.lineStyle(2, 0xff00ff, 0.5);
            graphics.strokeCircle(32, 32, 20);
            graphics.strokeCircle(32, 32, 12);
            graphics.generateTexture('obstacle_magnet', 64, 64);
        }
        
        // Default obstacle texture
        if (!this.textures.exists('obstacle_default')) {
            graphics.clear();
            graphics.fillStyle(0xff0000);
            graphics.fillRect(16, 16, 32, 32);
            graphics.lineStyle(2, 0xffff00);
            graphics.strokeRect(16, 16, 32, 32);
            graphics.generateTexture('obstacle_default', 64, 64);
        }
        
        console.log('[PreloadScene] Created obstacle textures');
    }

    /**
     * Create LARGE barrier textures - VERY BIG (200x400+ pixels)
     */
    createBarrierTextures(graphics) {
        // Solid wall barrier - large cyan rectangle
        if (!this.textures.exists('barrier_solid')) {
            graphics.clear();
            graphics.fillStyle(0x00ffff);
            graphics.fillRect(0, 0, 200, 400);
            // Add border for visibility
            graphics.lineStyle(4, 0xffffff);
            graphics.strokeRect(0, 0, 200, 400);
            // Add pattern
            graphics.lineStyle(2, 0x0088aa);
            for (let i = 0; i < 200; i += 20) {
                graphics.lineBetween(i, 0, i, 400);
            }
            graphics.generateTexture('barrier_solid', 200, 400);
        }

        // Sliding wall barrier - magenta with arrows
        if (!this.textures.exists('barrier_sliding')) {
            graphics.clear();
            graphics.fillStyle(0xff00ff);
            graphics.fillRect(0, 0, 200, 400);
            graphics.lineStyle(4, 0xffffff);
            graphics.strokeRect(0, 0, 200, 400);
            // Add arrow pattern to show movement
            graphics.lineStyle(3, 0xffaaff);
            for (let y = 50; y < 400; y += 100) {
                // Arrow pointing right
                graphics.beginPath();
                graphics.moveTo(50, y);
                graphics.lineTo(150, y);
                graphics.lineTo(140, y - 10);
                graphics.moveTo(150, y);
                graphics.lineTo(140, y + 10);
                graphics.strokePath();
            }
            graphics.generateTexture('barrier_sliding', 200, 400);
        }

        // Rising wall barrier - wide yellow barrier
        if (!this.textures.exists('barrier_rising')) {
            graphics.clear();
            graphics.fillStyle(0xffff00);
            graphics.fillRect(0, 0, 400, 200);
            graphics.lineStyle(4, 0xffffff);
            graphics.strokeRect(0, 0, 400, 200);
            // Add upward arrows
            graphics.lineStyle(3, 0xaaaa00);
            for (let x = 50; x < 400; x += 80) {
                graphics.beginPath();
                graphics.moveTo(x, 150);
                graphics.lineTo(x, 50);
                graphics.lineTo(x - 10, 60);
                graphics.moveTo(x, 50);
                graphics.lineTo(x + 10, 60);
                graphics.strokePath();
            }
            graphics.generateTexture('barrier_rising', 400, 200);
        }

        // Rotating wall barrier - orange square
        if (!this.textures.exists('barrier_rotating')) {
            graphics.clear();
            graphics.fillStyle(0xff6600);
            graphics.fillRect(0, 0, 300, 300);
            graphics.lineStyle(4, 0xffffff);
            graphics.strokeRect(0, 0, 300, 300);
            // Add rotation pattern
            graphics.lineStyle(3, 0xaa4400);
            graphics.beginPath();
            graphics.arc(150, 150, 100, 0, Math.PI * 2);
            graphics.strokePath();
            // Add rotation arrows
            for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 2) {
                const x = 150 + Math.cos(angle) * 80;
                const y = 150 + Math.sin(angle) * 80;
                graphics.fillCircle(x, y, 8);
            }
            graphics.generateTexture('barrier_rotating', 300, 300);
        }

        // Gate wall barrier - green with openings
        if (!this.textures.exists('barrier_gate')) {
            graphics.clear();
            graphics.fillStyle(0x00ff00);
            graphics.fillRect(0, 0, 200, 350);
            graphics.lineStyle(4, 0xffffff);
            graphics.strokeRect(0, 0, 200, 350);
            // Add gate pattern with openings
            graphics.fillStyle(0x008800);
            for (let y = 50; y < 350; y += 70) {
                graphics.fillRect(20, y, 160, 20);
            }
            // Add vertical bars
            graphics.lineStyle(3, 0x004400);
            for (let x = 40; x < 200; x += 40) {
                graphics.lineBetween(x, 0, x, 350);
            }
            graphics.generateTexture('barrier_gate', 200, 350);
        }

        // Default barrier texture
        if (!this.textures.exists('barrier_default')) {
            graphics.clear();
            graphics.fillStyle(0xff0000);
            graphics.fillRect(0, 0, 200, 400);
            graphics.lineStyle(4, 0xffffff);
            graphics.strokeRect(0, 0, 200, 400);
            graphics.generateTexture('barrier_default', 200, 400);
        }

        console.log('[PreloadScene] Created LARGE barrier textures - all 200x400+ pixels!');
    }
    
    /**
     * Create fallback player sprites in case actual sprites don't load
     */
    createFallbackPlayerSprites() {
        const graphics = this.add.graphics();
        
        // Create simple colored player sprites as fallback
        const playerColors = [0xff0000, 0x00ff00, 0x0000ff, 0xffff00, 0xff00ff, 0x00ffff];
        
        // Create fallback sprites for ALL 20 players
        for (let i = 0; i < 20; i++) {
            const key = `play_pug${i + 1}`;
            
            // Only create if texture doesn't already exist
            if (!this.textures.exists(key)) {
                graphics.clear();
                // Use different colors, cycling through available colors
                const colorIndex = i % playerColors.length;
                graphics.fillStyle(playerColors[colorIndex]);
                
                // Create simple car-like shape with unique marker
                graphics.fillRect(24, 8, 16, 48);    // Main body
                graphics.fillRect(16, 16, 32, 32);   // Middle section
                graphics.fillRect(20, 48, 24, 8);    // Rear
                
                // Add some details with player number
                graphics.fillStyle(0xffffff);
                graphics.fillRect(28, 12, 8, 8);     // Windscreen
                
                // Add a unique pattern for each player instead of text
                graphics.fillStyle(0x000000);
                // Create a unique pattern based on player number
                for (let j = 0; j < (i % 4) + 1; j++) {
                    graphics.fillRect(26 + j * 3, 30, 2, 2);  // Small dots as identifier
                }
                
                graphics.generateTexture(key, 64, 64);
                console.log(`[PreloadScene] Created fallback sprite for ${key} at 64x64`);
            }
        }
        
        graphics.destroy();
    }
    
    /**
     * Create minimal UI textures needed for buttons and UI elements
     */
    createMinimalUITextures() {
        const graphics = this.add.graphics();
        
        // Create button textures only
        const buttonWidth = 200;
        const buttonHeight = 50;
        
        // Button background
        graphics.fillStyle(0x2d2d2d);
        graphics.fillRect(0, 0, buttonWidth, buttonHeight);
        graphics.generateTexture('button_bg', buttonWidth, buttonHeight);
        
        // Button hover
        graphics.clear();
        graphics.fillStyle(0x3d3d3d);
        graphics.fillRect(0, 0, buttonWidth, buttonHeight);
        graphics.generateTexture('button_hover', buttonWidth, buttonHeight);
        
        // Button pressed
        graphics.clear();
        graphics.fillStyle(0x1d1d1d);
        graphics.fillRect(0, 0, buttonWidth, buttonHeight);
        graphics.generateTexture('button_pressed', buttonWidth, buttonHeight);
        
        // Create basic UI panel
        graphics.clear();
        graphics.fillStyle(0x2a2a2a, 0.9);
        graphics.fillRect(0, 0, 400, 300);
        graphics.generateTexture('ui_panel_basic', 400, 300);
        
        // Clean up
        graphics.destroy();
    }
    
    /**
     * Create meme collage background
     */
    createMemeCollageBackground() {
        const graphics = this.add.graphics();
        const width = 1920;
        const height = 1080;
        
        // Create a vibrant gradient base
        const gradient = this.add.graphics();
        const colors = [0xff00ff, 0x00ffff, 0xffff00, 0x00ff00, 0xff0088];
        
        // Draw multi-color gradient sections
        for (let i = 0; i < 5; i++) {
            gradient.fillStyle(colors[i], 0.3);
            gradient.fillRect((width / 5) * i, 0, width / 5, height);
        }
        
        // Add random meme-style elements
        const memeTexts = ['PUG RACING', 'MEMEX', 'M TOKEN', 'FOR 100', 'MOON', 'WAGMI'];
        const fonts = ['96px', '72px', '64px', '48px'];
        
        for (let i = 0; i < 20; i++) {
            const x = Math.random() * width;
            const y = Math.random() * height;
            const rotation = (Math.random() - 0.5) * 0.5;
            const text = this.add.text(x, y, memeTexts[Math.floor(Math.random() * memeTexts.length)], {
                fontSize: fonts[Math.floor(Math.random() * fonts.length)],
                fontFamily: 'Courier New, Impact, Arial Black',
                color: Phaser.Display.Color.RandomRGB().color32 >>> 0,
                fontWeight: 'bold',
                stroke: '#000000',
                strokeThickness: 4
            }).setOrigin(0.5).setRotation(rotation).setAlpha(0.2);
        }
        
        // Create texture from the current display
        gradient.generateTexture('meme_collage_bg', width, height);
        
        // Clean up temporary graphics
        gradient.destroy();
    }

    /**
     * Create pixel art sprites programmatically
     */
    createPixelSprites() {
        // First create the meme collage background
        this.createMemeCollageBackground();
        
        const graphics = this.add.graphics();
        
        // Player sprites (128x128 for consistency)
        const playerColors = [0xff0000, 0x00ff00, 0x0000ff, 0xffff00, 0xff00ff, 0x00ffff];
        playerColors.forEach((color, i) => {
            graphics.clear();
            graphics.fillStyle(color);
            
            // Create retro racing car sprite
            graphics.fillRect(32, 16, 64, 96);    // Main body
            graphics.fillRect(16, 48, 96, 48);     // Wings/sides
            graphics.fillRect(48, 0, 32, 32);      // Front/head
            
            // Add some details
            graphics.fillStyle(0xffffff);
            graphics.fillRect(56, 8, 16, 16);      // Windscreen
            graphics.fillRect(40, 112, 48, 8);     // Spoiler
            
            graphics.generateTexture(`player${i}`, 128, 128);
        });
        
        // M Token sprite (128x128)
        graphics.clear();
        graphics.fillStyle(0xffd700);
        graphics.fillRect(32, 0, 64, 128);   // Vertical bar of M (scaled 4x)
        graphics.fillRect(0, 32, 128, 64);   // Horizontal bar of M (scaled 4x)
        graphics.fillRect(48, 16, 32, 32);    // Center highlight (scaled 4x)
        graphics.generateTexture('mtoken', 128, 128);
        
        // Booster sprites (16x16)
        const boosterTypes = [
            { name: 'antenna', color: 0x00ff00 },
            { name: 'twitter', color: 0x1da1f2 },
            { name: 'memex', color: 0xff00ff },
            { name: 'poo', color: 0x8b4513 },
            { name: 'toilet_paper', color: 0xffffff },
            { name: 'toilet', color: 0x4169e1 },
            { name: 'banana', color: 0xffff00 },
            { name: 'king_kong', color: 0x800080 }
        ];
        
        boosterTypes.forEach((booster) => {
            graphics.clear();
            graphics.fillStyle(booster.color);
            graphics.fillCircle(8, 8, 6);
            
            // Add some detail based on type
            graphics.fillStyle(0xffffff);
            switch(booster.name) {
                case 'antenna':
                    graphics.fillRect(7, 4, 2, 8);
                    break;
                case 'banana':
                    graphics.fillRect(6, 6, 4, 2);
                    break;
                case 'poo':
                    graphics.fillCircle(8, 6, 2);
                    break;
                default:
                    graphics.fillCircle(8, 8, 3);
            }
            
            graphics.generateTexture(`booster_${booster.name}`, 16, 16);
        });
        
        // Skill sprites (32x32)
        const skillTypes = [
            { name: 'thunder', color: 0xffff00 },
            { name: 'fire', color: 0xff4500 },
            { name: 'bubble', color: 0x00bfff },
            { name: 'magnet', color: 0xff1493 },
            { name: 'teleport', color: 0x9400d3 }
        ];
        
        skillTypes.forEach((skill) => {
            graphics.clear();
            graphics.fillStyle(0x333333);
            graphics.fillRect(4, 4, 24, 24);
            
            graphics.fillStyle(skill.color);
            graphics.fillRect(8, 8, 16, 16);
            
            // Add skill-specific icons
            graphics.fillStyle(0xffffff);
            switch(skill.name) {
                case 'thunder':
                    graphics.fillRect(14, 8, 4, 8);
                    graphics.fillRect(12, 12, 8, 4);
                    break;
                case 'fire':
                    graphics.fillRect(12, 10, 8, 12);
                    break;
                case 'bubble':
                    graphics.fillCircle(16, 16, 6);
                    break;
                case 'magnet':
                    graphics.fillRect(12, 10, 8, 8);
                    graphics.fillRect(10, 12, 12, 4);
                    break;
                case 'teleport':
                    graphics.fillCircle(16, 16, 8);
                    graphics.fillStyle(skill.color);
                    graphics.fillCircle(16, 16, 4);
                    break;
            }
            
            graphics.generateTexture(`skill_${skill.name}`, 32, 32);
        });
        
        // UI Elements - Transparent buttons with colored outlines
        graphics.clear();
        graphics.fillStyle(0x000000, 0.3); // Semi-transparent black
        graphics.fillRoundedRect(0, 0, 200, 50, 8);
        graphics.lineStyle(3, 0x00ff00, 1); // Green outline
        graphics.strokeRoundedRect(0, 0, 200, 50, 8);
        graphics.generateTexture('button_green', 200, 50);
        
        graphics.clear();
        graphics.fillStyle(0x000000, 0.3);
        graphics.fillRoundedRect(0, 0, 200, 50, 8);
        graphics.lineStyle(3, 0xff0000, 1); // Red outline
        graphics.strokeRoundedRect(0, 0, 200, 50, 8);
        graphics.generateTexture('button_red', 200, 50);
        
        graphics.clear();
        graphics.fillStyle(0x000000, 0.3);
        graphics.fillRoundedRect(0, 0, 200, 50, 8);
        graphics.lineStyle(3, 0x0000ff, 1); // Blue outline
        graphics.strokeRoundedRect(0, 0, 200, 50, 8);
        graphics.generateTexture('button_blue', 200, 50);
        
        graphics.clear();
        graphics.fillStyle(0x000000, 0.3);
        graphics.fillRoundedRect(0, 0, 200, 50, 8);
        graphics.lineStyle(3, 0xffff00, 1); // Yellow outline
        graphics.strokeRoundedRect(0, 0, 200, 50, 8);
        graphics.generateTexture('button_yellow', 200, 50);
        
        graphics.destroy();
    }

    /**
     * Load external assets (sounds, images, etc.)
     */
    loadExternalAssets() {
        // Use production-friendly paths (relative to public folder)
        const soundPath = 'assets/sounds/';
        const iconPath = 'assets/ui/icons/';
        const spritePath = 'assets/sprites/';
        const backgroundPath = 'assets/maps/backgrounds/';
        
        // Set loading to not fail on missing files
        this.load.on('filecomplete', (key, type, data) => {
            console.log(`[PreloadScene] Loaded: ${key} (${type})`);
        });
        
        this.load.on('loaderror', (file) => {
            console.warn(`[PreloadScene] Failed to load: ${file.src} - Continuing without this asset`);
            console.warn(`[PreloadScene] File details - Key: ${file.key}, Type: ${file.type}, URL: ${file.url}`);
            
            // Retry loading for player sprites
            if (file.key && file.key.startsWith('play_pug')) {
                console.log(`[PreloadScene] Retrying load for ${file.key}...`);
                const retryUrl = file.url || file.src;
                
                // Try loading with a direct image creation as fallback
                this.time.delayedCall(100, () => {
                    if (!this.textures.exists(file.key)) {
                        console.log(`[PreloadScene] Creating texture from URL for ${file.key}`);
                        try {
                            // Try to load using a different method
                            const img = new Image();
                            img.crossOrigin = 'anonymous';
                            img.onload = () => {
                                console.log(`[PreloadScene] Successfully loaded ${file.key} via Image element`);
                                this.textures.addImage(file.key, img);
                            };
                            img.onerror = () => {
                                console.error(`[PreloadScene] Failed to load ${file.key} even with retry`);
                                // Create fallback as last resort
                                this.createFallbackPlayerSprite(file.key);
                            };
                            img.src = retryUrl;
                        } catch (e) {
                            console.error(`[PreloadScene] Exception loading ${file.key}:`, e);
                            this.createFallbackPlayerSprite(file.key);
                        }
                    }
                });
            }
            // Don't let missing assets break the game
        });
        
        // Try to load actual sprite assets if they exist
        try {
            // Load 20 player sprites with consistent naming (play_pug1 through play_pug20)
            const playerSpritePath = `${spritePath}players/default/`;
            this.load.image('play_pug1', `${playerSpritePath}play_pug1.png`);
            this.load.image('play_pug2', `${playerSpritePath}play_pug2.png`);
            this.load.image('play_pug3', `${playerSpritePath}play_pug3.png`);
            this.load.image('play_pug4', `${playerSpritePath}play_pug4.png`);
            this.load.image('play_pug5', `${playerSpritePath}play_pug5.png`);
            this.load.image('play_pug6', `${playerSpritePath}play_pug6.png`);
            this.load.image('play_pug7', `${playerSpritePath}play_pug7.png`);
            this.load.image('play_pug8', `${playerSpritePath}play_pug8.png`);
            this.load.image('play_pug9', `${playerSpritePath}play_pug9.png`);
            this.load.image('play_pug10', `${playerSpritePath}play_pug10.png`);
            this.load.image('play_pug11', `${playerSpritePath}play_pug11.png`);
            this.load.image('play_pug12', `${playerSpritePath}play_pug12.png`);
            this.load.image('play_pug13', `${playerSpritePath}play_pug13.png`);
            this.load.image('play_pug14', `${playerSpritePath}play_pug14.png`);
            this.load.image('play_pug15', `${playerSpritePath}play_pug15.png`);
            this.load.image('play_pug16', `${playerSpritePath}play_pug16.png`);
            this.load.image('play_pug17', `${playerSpritePath}play_pug17.png`);
            this.load.image('play_pug18', `${playerSpritePath}play_pug18.png`);
            this.load.image('play_pug19', `${playerSpritePath}play_pug19.png`);
            this.load.image('play_pug20', `${playerSpritePath}play_pug20.png`);
            
            console.log('[PreloadScene] Loading 20 player sprites (play_pug1 through play_pug20)');
            
            // Load booster sprites - 64x64 assets matching the defined types
            this.load.image('booster_banana', `${spritePath}boosters/banana.png`);
            this.load.image('booster_toilet_paper', `${spritePath}boosters/toilet-paper.png`);
            this.load.image('booster_poo', `${spritePath}boosters/shit2.png`);
            this.load.image('booster_king_kong', `${spritePath}boosters/diamondfist.png`);
            this.load.image('booster_antenna', `${spritePath}boosters/Crik-cutout2.png`);
            // Create aliases for other booster types that use existing sprites
            this.load.image('booster_twitter', `${spritePath}boosters/Crik-cutout2.png`);
            this.load.image('booster_memex', `${spritePath}boosters/diamondfist.png`);
            this.load.image('booster_toilet', `${spritePath}boosters/toilet-paper.png`);
            
            // Load obstacle sprites - 64x64 and 128x128 assets
            const obstaclePath = `${spritePath}obstacles/`;
            this.load.image('obstacle_oil', `${obstaclePath}obstacle_oil.png`);
            this.load.image('obstacle_mud', `${obstaclePath}obstacle_mud.png`);
            this.load.image('obstacle_rock', `${obstaclePath}obstacle_rock.png`);
            this.load.image('obstacle_spikes', `${obstaclePath}obstacle_spikes.png`);
            this.load.image('obstacle_barrier', `${obstaclePath}obstacle_barrier.png`);
            this.load.image('obstacle_blade', `${obstaclePath}obstacle_blade.png`);
            this.load.image('obstacle_laser', `${obstaclePath}obstacle_laser.png`);
            this.load.image('obstacle_trapdoor', `${obstaclePath}obstacle_trapdoor.png`);
            this.load.image('obstacle_ice', `${obstaclePath}obstacle_ice.png`);
            this.load.image('obstacle_magnet', `${obstaclePath}obstacle_magnet.png`);
            this.load.image('obstacle_default', `${obstaclePath}obstacle_default.png`);
            
            // Load skill sprites - 64x64 assets
            this.load.image('skill_thunder', `${spritePath}skills/thuner.png`);
            this.load.image('skill_fire', `${spritePath}skills/fire.png`);
            this.load.image('skill_bubble', `${spritePath}skills/bubble.png`);
            this.load.image('skill_magnet', `${spritePath}skills/magnet.png`);
            this.load.image('skill_teleport', `${spritePath}skills/teleport.png`);
            
            // Load M token
            this.load.image('mtoken_real', `${iconPath}M token.png`);
            
            // Note: Player sprites already loaded above (player_pug1 through player_pug20)
            
            // Load UI panel
            this.load.image('ui_panel', `${spritePath}ui/panels/1500x500.jpeg`);
            
            // Load track templates (including new template11 with wider lanes)
            const trackPath = 'assets/maps/tracks/';
            for (let i = 1; i <= 11; i++) {
                this.load.image(`track_template${i}`, `${trackPath}template${i}.png`);
                console.log(`[PreloadScene] Loading track template${i}`);
            }
            
            // Load game backgrounds with correct extensions
            const bgPath = 'assets/maps/backgrounds/';
            const bgFiles = {
                1: 'jpeg', 2: 'jpeg', 3: 'png', 4: 'jpeg', 5: 'jpeg',
                6: 'jpeg', 8: 'png', 9: 'png', 10: 'jpeg', 
                11: 'png', 12: 'png', 13: 'jpg', 14: 'png', 15: 'png', 16: 'png',
                17: 'png', 18: 'png', 19: 'png', 20: 'png', 21: 'png'
            };
            
            // Load backgrounds with both formats for compatibility
            for (let i in bgFiles) {
                const ext = bgFiles[i];
                // Load with 'backgrounds/mapX' key for new system
                this.load.image(`backgrounds/map${i}`, `${bgPath}map${i}.${ext}`);
                // Also keep old format for backwards compatibility
                this.load.image(`game_bg${i}`, `${bgPath}map${i}.${ext}`);
                console.log(`[PreloadScene] Loading game background map${i}.${ext}`);
            }
            
            // Load all fixed map backgrounds (13 total, skipping fixed_map6 which doesn't exist)
            const fixedMapNumbers = [1, 2, 3, 4, 5, 7, 8, 9, 10, 11, 12, 13, 14];
            fixedMapNumbers.forEach(num => {
                this.load.image(`fixed_map${num}`, `${bgPath}fixed_map${num}.png`);
                console.log(`[PreloadScene] Loading fixed map background: fixed_map${num}.png`);
            });
            
            // Store available backgrounds for random selection
            this.game.registry.set('availableBackgrounds', fixedMapNumbers.map(num => `fixed_map${num}`));
            
            // Load lobby/menu backgrounds
            console.log('[PreloadScene] Loading menu backgrounds...');
            // Load background images with static paths - more reliable than webpack imports
            const bgBasePath = 'assets/backgrounds/';
            console.log('[PreloadScene] Loading from static path:', bgBasePath);
            
            this.load.image('main_menu_bg', bgBasePath + 'main-menu-background.png');
            this.load.image('lobby_bg', bgBasePath + 'race-lobby.jpeg');
            this.load.image('login_bg', bgBasePath + 'main-menu-background.png');
            
            console.log('[PreloadScene] Background load URLs:', {
                main_menu_bg: bgBasePath + 'main-menu-background.png',
                lobby_bg: bgBasePath + 'race-lobby.jpeg',
                login_bg: bgBasePath + 'main-menu-background.png'
            });
            
            // Add load complete handler for debugging
            this.load.on('filecomplete-image-main_menu_bg', () => {
                console.log('[PreloadScene] ✅ Successfully loaded main_menu_bg');
            });
            this.load.on('filecomplete-image-lobby_bg', () => {
                console.log('[PreloadScene] ✅ Successfully loaded lobby_bg');
            });
            this.load.on('filecomplete-image-login_bg', () => {
                console.log('[PreloadScene] ✅ Successfully loaded login_bg');
            });
            
            // Add comprehensive load handlers for debugging
            this.load.on('loaderror', (file) => {
                console.error('[PreloadScene] ❌ Failed to load:', file.src, '- Key:', file.key);
                console.error('[PreloadScene] File type:', file.type, 'URL:', file.url);
                console.error('[PreloadScene] Full file details:', {
                    key: file.key,
                    url: file.url,
                    src: file.src,
                    type: file.type,
                    state: file.state
                });
                
                // Handle background image failures specifically
                if (file.key === 'main_menu_bg' || file.key === 'login_bg' || file.key === 'lobby_bg') {
                    console.error(`[PreloadScene] 🚨 CRITICAL: Background image ${file.key} failed to load!`);
                    console.error(`[PreloadScene] This will cause fallback to animated background`);
                    
                    // Try alternative loading approaches
                    if (file.key === 'main_menu_bg') {
                        console.warn('[PreloadScene] Attempting backup load for main_menu_bg...');
                        // Try loading with explicit crossOrigin setting
                        this.load.image('main_menu_bg_backup', {
                            key: 'main_menu_bg_backup',
                            url: bgBasePath + 'main-menu-background.png',
                            crossOrigin: 'anonymous'
                        });
                    }
                }
                
                // Create fallback for failed player sprites
                if (file.key && file.key.startsWith('play_pug')) {
                    console.warn(`[PreloadScene] Creating fallback for ${file.key}`);
                    this.createFallbackPlayerSprite(file.key);
                }
            });
            
            // Track successful loads
            this.load.on('filecomplete', (key, type, data) => {
                if (key.startsWith('play_pug') || key.startsWith('track_') || key.startsWith('game_bg')) {
                    console.log(`[PreloadScene] ✅ Loaded: ${key} (${type})`);
                }
                
                // Specifically track background images
                if (key === 'main_menu_bg' || key === 'login_bg' || key === 'lobby_bg') {
                    console.log(`[PreloadScene] ✅ Background image loaded: ${key}`);
                    if (data && data.source && data.source[0]) {
                        console.log(`[PreloadScene] Image dimensions: ${data.source[0].width}x${data.source[0].height}`);
                    } else {
                        console.log(`[PreloadScene] Image data structure:`, data);
                    }
                }
            });
        } catch (error) {
            console.warn('[PreloadScene] Error setting up asset loading:', error);
            // Continue with programmatically generated assets
        }
        
        // Engine/race sounds - these may not exist yet
        if (!this.cache.audio.exists('engine_loop')) {
            // Try to load but don't fail if missing
            this.load.audio('engine_loop', [`${soundPath}engine.mp3`, `${soundPath}engine.ogg`]);
            this.load.audio('booster_collect', [`${soundPath}booster.mp3`, `${soundPath}booster.ogg`]);
            this.load.audio('skill_activate', [`${soundPath}skill.mp3`, `${soundPath}skill.ogg`]);
            this.load.audio('race_start', [`${soundPath}start.mp3`, `${soundPath}start.ogg`]);
            this.load.audio('race_win', [`${soundPath}win.mp3`, `${soundPath}win.ogg`]);
        }
        
        // UI sounds
        if (!this.cache.audio.exists('ui_click')) {
            this.load.audio('ui_click', [`${soundPath}click.mp3`, `${soundPath}click.ogg`]);
        }
    }

    /**
     * Ensure button textures exist
     */
    ensureButtonTextures() {
        // Check and create button textures if they don't exist
        const buttons = ['button_green', 'button_blue', 'button_red', 'button_yellow'];
        const colors = [0x00ff00, 0x0000ff, 0xff0000, 0xffff00];
        
        buttons.forEach((buttonKey, index) => {
            if (!this.textures.exists(buttonKey)) {
                console.log(`[PreloadScene] Creating ${buttonKey} texture`);
                const graphics = this.add.graphics();
                // Transparent fill with colored outline
                graphics.fillStyle(0x000000, 0.3); // Semi-transparent black
                graphics.fillRoundedRect(0, 0, 200, 50, 8);
                graphics.lineStyle(3, colors[index], 1); // Colored outline
                graphics.strokeRoundedRect(0, 0, 200, 50, 8);
                graphics.generateTexture(buttonKey, 200, 50);
                graphics.destroy();
            }
        });
    }

    /**
     * Create custom player sprite from uploaded image
     * @param {string} key - Texture key
     * @param {HTMLCanvasElement} canvas - Canvas with custom image
     */
    createCustomPlayerSprite(key, canvas) {
        if (this.textures.exists(key)) {
            this.textures.remove(key);
        }
        this.textures.addCanvas(key, canvas);
    }

    /**
     * Get list of available player sprites
     * @returns {Array} Array of sprite keys
     */
    getPlayerSpriteKeys() {
        const keys = [];
        for (let i = 0; i < 6; i++) {
            keys.push(`player${i}`);
        }
        return keys;
    }

    /**
     * Get list of available booster sprites
     * @returns {Array} Array of booster data
     */
    getBoosterTypes() {
        return [
            { name: 'antenna', key: 'booster_antenna', speedMultiplier: 1.5 },
            { name: 'twitter', key: 'booster_twitter', speedMultiplier: 1.4 },
            { name: 'memex', key: 'booster_memex', speedMultiplier: 1.6 },
            { name: 'poo', key: 'booster_poo', speedMultiplier: 0.8 },
            { name: 'toilet_paper', key: 'booster_toilet_paper', speedMultiplier: 1.3 },
            { name: 'toilet', key: 'booster_toilet', speedMultiplier: 1.2 },
            { name: 'banana', key: 'booster_banana', speedMultiplier: 1.7 },
            { name: 'king_kong', key: 'booster_king_kong', speedMultiplier: 2.0 }
        ];
    }

    /**
     * Get list of available skill types
     * @returns {Array} Array of skill data
     */
    getSkillTypes() {
        return [
            { name: 'thunder', key: 'skill_thunder', duration: 3000, description: 'Paralyze 3 random opponents' },
            { name: 'fire', key: 'skill_fire', duration: 5000, description: 'Burn 2 characters, causing slowdown' },
            { name: 'bubble', key: 'skill_bubble', duration: 8000, description: 'Bounce off other players' },
            { name: 'magnet', key: 'skill_magnet', duration: 5000, description: 'Attract to nearby racers' },
            { name: 'teleport', key: 'skill_teleport', duration: 0, description: 'Teleport all players randomly' }
        ];
    }
    
    /**
     * Create fallback sprite for failed player texture
     * @param {string} key - The texture key that failed to load
     */
    createFallbackPlayerSprite(key) {
        const graphics = this.add.graphics();
        const colors = [
            0xff0000, 0x00ff00, 0x0000ff, 0xffff00, 0xff00ff, 0x00ffff,
            0xff8800, 0x88ff00, 0x0088ff, 0xff0088, 0x8800ff, 0x00ff88,
            0xffffff, 0x888888, 0x444444, 0xffcc00, 0xcc00ff, 0x00ccff,
            0xcc0000, 0x00cc00
        ];
        
        // Extract number from key (e.g., "play_pug15" -> 15)
        const match = key.match(/\d+$/);
        const index = match ? parseInt(match[0]) - 1 : 0;
        const color = colors[index % colors.length];
        
        // Create 32x32 colored square
        graphics.fillStyle(color);
        graphics.fillRect(0, 0, 32, 32);
        
        // Add simple details for visibility
        graphics.fillStyle(0xffffff);
        graphics.fillRect(8, 4, 16, 6); // "Eyes"
        graphics.fillRect(12, 20, 8, 4); // "Mouth"
        
        // Generate texture
        graphics.generateTexture(key, 32, 32);
        graphics.destroy();
        
        console.log(`[PreloadScene] Created fallback sprite for ${key} with color 0x${color.toString(16)}`);
    }

    /**
     * Validate background textures are loaded properly
     */
    validateBackgroundTextures() {
        console.log('[PreloadScene] 🖼️ Validating background textures...');
        
        const backgroundTextures = ['main_menu_bg', 'login_bg', 'lobby_bg'];
        
        backgroundTextures.forEach(key => {
            if (this.textures.exists(key)) {
                const texture = this.textures.get(key);
                if (texture.source && texture.source[0]) {
                    const source = texture.source[0];
                    console.log(`[PreloadScene] ✅ ${key} loaded:`, {
                        width: source.width,
                        height: source.height,
                        complete: source.complete,
                        src: source.image?.src || 'unknown'
                    });
                    
                    // Verify it's not just a blank/error texture
                    if (source.width === 1 && source.height === 1) {
                        console.error(`[PreloadScene] 🚨 ${key} appears to be a 1x1 error texture!`);
                    }
                } else {
                    console.warn(`[PreloadScene] ⚠️ ${key} texture has no source data`);
                }
                
                // Check if backup texture was used
                const backupKey = key + '_backup';
                if (this.textures.exists(backupKey)) {
                    console.log(`[PreloadScene] 📦 Backup texture ${backupKey} also available`);
                }
                
            } else {
                console.error(`[PreloadScene] ❌ MISSING: ${key} texture not found!`);
                console.error(`[PreloadScene] This will cause fallback to animated background`);
                
                // Check for backup texture
                const backupKey = key + '_backup';
                if (this.textures.exists(backupKey)) {
                    console.warn(`[PreloadScene] 🔄 Found backup texture: ${backupKey}`);
                    // Copy backup to main key
                    const backupTexture = this.textures.get(backupKey);
                    this.textures.addTextureSource(key, backupTexture.source[0]);
                    console.log(`[PreloadScene] ✅ Copied backup texture to ${key}`);
                } else {
                    console.error(`[PreloadScene] 💥 No backup texture available for ${key}`);
                }
            }
        });
        
        // Additional validation - check the texture manager state
        console.log('[PreloadScene] Texture manager state:', {
            totalTextures: Object.keys(this.textures.list).length,
            backgroundsFound: backgroundTextures.filter(key => this.textures.exists(key)).length,
            textureKeys: Object.keys(this.textures.list)
        });
    }

    /**
     * Attempt emergency background loading if primary load failed
     */
    attemptEmergencyBackgroundLoad() {
        console.log('[PreloadScene] 🚨 Checking if emergency background load is needed...');
        
        if (!this.textures.exists('main_menu_bg') || 
            (this.textures.exists('main_menu_bg') && 
             this.textures.get('main_menu_bg').source && 
             this.textures.get('main_menu_bg').source[0] && 
             this.textures.get('main_menu_bg').source[0].width === 1)) {
            
            console.warn('[PreloadScene] 🆘 Emergency background load required!');
            
            // Try loading with different approaches
            const bgBasePath = 'assets/backgrounds/';
            const attempts = [
                // Attempt 1: Try absolute path
                () => {
                    console.log('[PreloadScene] Emergency attempt 1: Absolute path');
                    return new Promise((resolve) => {
                        const img = new Image();
                        img.crossOrigin = 'anonymous';
                        img.onload = () => {
                            // Create texture directly from image
                            this.textures.addImage('main_menu_bg_emergency', img);
                            console.log('[PreloadScene] ✅ Emergency load successful via absolute path');
                            resolve(true);
                        };
                        img.onerror = () => {
                            console.error('[PreloadScene] ❌ Emergency attempt 1 failed');
                            resolve(false);
                        };
                        img.src = window.location.origin + '/' + bgBasePath + 'main-menu-background.png';
                    });
                },
                
                // Attempt 2: Try without crossOrigin
                () => {
                    console.log('[PreloadScene] Emergency attempt 2: No crossOrigin');
                    return new Promise((resolve) => {
                        const img = new Image();
                        img.onload = () => {
                            this.textures.addImage('main_menu_bg_emergency2', img);
                            console.log('[PreloadScene] ✅ Emergency load successful without crossOrigin');
                            resolve(true);
                        };
                        img.onerror = () => {
                            console.error('[PreloadScene] ❌ Emergency attempt 2 failed');
                            resolve(false);
                        };
                        img.src = bgBasePath + 'main-menu-background.png';
                    });
                }
            ];
            
            // Try the first emergency method synchronously
            console.log('[PreloadScene] Attempting synchronous emergency load...');
            attempts[0]().then(success => {
                if (success) {
                    console.log('[PreloadScene] 🎉 Emergency background load successful!');
                } else {
                    console.error('[PreloadScene] 💀 All emergency load attempts failed');
                }
            });
        } else {
            console.log('[PreloadScene] ✅ Emergency load not needed - background texture appears valid');
        }
    }

    /**
     * Validate all required assets are available before scene transition
     */
    validateRequiredAssets() {
        console.log('[PreloadScene] 🔍 Validating required assets...');
        
        let missingAssets = [];
        let availableSprites = 0;
        let fallbackSprites = 0;
        
        // Check all 20 player sprites
        for (let i = 1; i <= 20; i++) {
            const spriteKey = `play_pug${i}`;
            if (this.textures.exists(spriteKey)) {
                availableSprites++;
                const texture = this.textures.get(spriteKey);
                if (texture.source && texture.source[0] && 
                    texture.source[0].width === 32 && texture.source[0].height === 32) {
                    fallbackSprites++; // This is likely a fallback sprite
                }
            } else {
                missingAssets.push(spriteKey);
                console.warn(`[PreloadScene] ⚠️ Missing sprite: ${spriteKey}`);
                // Create fallback immediately
                this.createFallbackPlayerSprite(spriteKey);
                availableSprites++;
                fallbackSprites++;
            }
        }
        
        // Check essential sprites
        const essentialSprites = ['mtoken'];
        essentialSprites.forEach(key => {
            if (!this.textures.exists(key)) {
                missingAssets.push(key);
                console.warn(`[PreloadScene] ⚠️ Missing essential sprite: ${key}`);
            }
        });
        
        // Check button textures
        const buttonTextures = ['button_green', 'button_blue', 'button_red', 'button_yellow'];
        buttonTextures.forEach(key => {
            if (!this.textures.exists(key)) {
                missingAssets.push(key);
                console.warn(`[PreloadScene] ⚠️ Missing button texture: ${key}`);
            }
        });
        
        // Log validation results
        console.log('[PreloadScene] 📊 Asset Validation Results:');
        console.log(`  ✅ Player Sprites: ${availableSprites}/20 available`);
        console.log(`  🔄 Fallback Sprites: ${fallbackSprites}`);
        console.log(`  🎯 Essential Assets: ${essentialSprites.length - missingAssets.filter(k => essentialSprites.includes(k)).length}/${essentialSprites.length}`);
        console.log(`  🔘 Button Textures: ${buttonTextures.length - missingAssets.filter(k => buttonTextures.includes(k)).length}/${buttonTextures.length}`);
        console.log(`  ⚠️ Missing Assets: ${missingAssets.length}`);
        
        if (missingAssets.length > 0) {
            console.warn('[PreloadScene] ⚠️ Some assets are missing, but game can continue with fallbacks');
        } else {
            console.log('[PreloadScene] ✅ All required assets validated successfully');
        }
        
        return missingAssets.length === 0;
    }
}